<template>
  <div class="history_container">
    <portal-target name="portal-filter-second_popup" />
    <div class="header">
      <div class="header-left">
        <div class="header-icon">
          <svg-sprite
            :color="'#00564C'"
            :name="'time'"
          />
        </div>
        <div
          v-once
          class="header-title"
          v-html="locales('history_header_title')"
        />
      </div>
      <div
        class="header-right"
      >
        <zoom-slider
          v-if="isGanttInstanceLoaded"
          :value="zoom"
          :zoom-size="'102px'"
          @onChange="zoomChange"
        />
        <vgp-button
          v-if="isGanttInstanceLoaded && accessToRestore"
          class="header-restore-button"
          type="primary"
          small
          :label="locales('history_restore_version')"
          :disabled="!selectedEvent && !restoreButtonClicked"
          @onClick="restoreHistory"
        />
        <vgp-button
          v-if="isGanttInstanceLoaded && accessToBaseline"
          type="text"
          small
          :icon="{name: 'save', type:'regular', size: 24}"
          :label="locales('history_window_header_baseline')"
          :disabled="!selectedEvent"
          @onClick="saveAsBaseline"
        />
        <div class="header-icon-buttons">
          <div class="header-close">
            <icon-button
              :icon="{ name:'help' }"
              size="small"
              @onClick="onClickInfo"
            />
          </div>
          <div class="header-close">
            <icon-button
              :icon="{ name:'close-1' }"
              size="small"
              @onClick="closeHistory"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="body">
      <div :class="['gantt', isRightBarShown ? '' : 'gantt_expanded' ]">
        <gantt-view
          @ready="initGanttInstance"
        />
      </div>
      <div
        v-if="isGanttInstanceLoaded"
        v-show="isRightBarShown"
        :class="['right-bar', !isRightBarShown ? 'collapsed' : '', filterIsOpen ? 'filter-open' : '']"
      >
        <right-bar
          :events="sortedEvents"
          :selected-event="selectedEvent"
          :active-filter="activeFilter"
          :prepared-pin-date="dateForPin"
          @toggleSidebar="rightBarToggle"
          @filterClick="toggleFilter"
          @selectEvent="updateGanttByEvent"
          @search="i => searchStr = i"
        />
      </div>
      <div
        v-if="!isRightBarShown"
        class="history_expand_button"
      >
        <icon-button
          :icon="{ name:'collaps-2', size: 24, type: 'bold' }"
          :colors="iconColors"
          :size="'middle'"
          :label="locales('history_expand_settings')"
          @onClick="rightBarToggle"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import projectsModel from '$$projectModel';
import ganttViewConstructor from '../ganttView/init';
import constants from '$$helpers/constants';
import GanttView from '../ganttView/ganttView.vue';
import RightBar from './rightBar/RightBar.vue';
import iconButton from '../common/VueComponents/globalButton/withIcon/iconButton.vue';
import ZoomSlider from '../header/include/customZoomSlider.vue';
import moment from '../../libs/moment';
import helpPopup from '../help/helpPopup';
import rights from '../../components/rights';
import globalToast from '$$vueCmp/toast';
import historyHelpers from './helpers';
import ganttPreloader from '../ganttView/helpers/preloader';

export default {
  name: 'History',
  components: {
    ZoomSlider,
    iconButton,
    GanttView,
    RightBar,
  },
  data() {
    return {
      locales: __,
      isGanttInstanceLoaded: false,
      isRightBarShown: true,
      iconColors: {
        color: '#1565C0',
        labelColor: '#1565C0',
        activeColor: '#11519A',
        hoverBg: '#D0E0F2',
        activeBg: '#A1C1E6',
        activeHoverBg: '#D0E0F2',
        bg: '#D0E0F2',
      },
      events: [],
      sortedEvents: null,
      selectedEvent: null,
      searchStr: '',
      startEndDates: {},
      activeFilter: false,
      dateForPin: null,
      isLoading: false,
      restoreButtonClicked: false,
    };
  },
  computed: {
    zoom() {
      return this.$store.getters['headerView/zoom'];
    },
    projectId() {
      return +this.$route.params.projectId;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    accessToRestore() {
      const right = rights.project.hasRight(this.projectId, 'history_restore');
      const projectData = projectsModel.getProjectDataById(this.projectId);

      return right && projectData && !projectData.is_archived && !projectData.is_jira;
    },
    accessToBaseline() {
      const right = rights.project.hasRight(this.projectId, 'gantt_chart');
      const projectData = projectsModel.getProjectDataById(this.projectId);

      return right && projectData && !projectData.is_archived;
    },
    filterIsOpen() {
      return this.$store.getters['filter/isOpen'];
    },
  },
  watch: {
    searchStr(data) {
      this.preparedEventData(this.events);
    },
    currentFilter(val) {
      if (!val.properties.date && !val.properties.time) {
        this.setFiltersDate();
      }
      this.preparedEventData(this.events);
    },
    isLoading(val) {
      if (val) {
        ganttPreloader.show();
      } else {
        ganttPreloader.hide();
      }
    },
  },
  beforeUnmount() {
    this.$store.dispatch('history/resetState');
  },
  beforeCreate() {
    this.$store.commit('setAppMode', constants.APP_MODES.HISTORY);
  },
  created() {
    this.ganttInstance = null;
    this.hideLeftSideBarAndFooter();
  },
  mounted() {
    userExtAnalytics.log('project_history_open');
  },
  methods: {
    hideLeftSideBarAndFooter() {
      const leftSideBar = $$('leftSideBar');
      const footer = $$('footer');

      leftSideBar.$view.classList.add('left_sidebar_down_index');
      footer.$view.classList.add('footer_down_index');
    },
    async initGanttInstance() {
      this.isLoading = true;
      await this.$store.dispatch('history/loadData', { ganttId: +this.$route.params.projectId });
      this.isLoading = false;

      this.events = this.$store.getters['history/getEvents'];
      this.setFiltersDate();
      this.preparedEventData(this.events);

      const lastObjectWithAzureKey = this.events?.find(el => el.azure_key);

      if (lastObjectWithAzureKey) {
        await this.updateGanttByEvent(lastObjectWithAzureKey);
        this.isGanttInstanceLoaded = true;
      }
    },
    toggleFilter() {
      const toggle = !this.filterIsOpen;

      setTimeout(() => {
        this.$store.commit('filter/toggleFilter', toggle);
      }, 0);
    },
    closeHistory() {
      const mode = this.$store.getters['history/prevFrom'] || 'gantt';

      this.$store.commit('setAppMode', constants.APP_MODES.BASE);
      this.$router.push({
        name: 'project',
        params: { ...this.$route.params, mode },
      });
    },
    rightBarToggle() {
      if (this.filterIsOpen) {
        this.toggleFilter();
      }

      this.isRightBarShown = !this.isRightBarShown;
    },
    zoomChange: _.debounce(function (value) {
      this.ganttInstance.callEvent('history:onZoomChange', [constants.ZOOM.zoomValueToMode[value]]);
    }, 300),
    onClickInfo() {
      helpPopup.showHelpBlock('', 'historyHelpPopup');
      this.$store.dispatch('user/updateActivityStatus', {
        activityName: 'historyHelpPopup',
        status: 1,
      });
    },
    saveAsBaseline() {
      if (!this.selectedEvent) {
        return;
      }

      this.$store.dispatch('baselines/createBaseline', {
        ganttId: +this.$route.params.projectId,
        eventId: +this.selectedEvent.id,
      });

      userExtAnalytics.log('create_baseline_done', { from: 'history' });
      globalToast.success(this.locales('baseline_was_saved'));

      return false;
    },
    async restoreHistory() {
      const eventId = +this.selectedEvent.id;

      if (this.restoreButtonClicked) return;

      this.restoreButtonClicked = true;
      await this.$store.dispatch('history/reloadHistory', { eventId, ganttId: +this.projectId });

      userExtAnalytics.log('project_history_restore');

      return false;
    },
    async updateGanttByEvent(item) {
      if (!item || !item.azure_key || item.id === this.selectedEvent?.id) {
        return;
      }

      this.selectedEvent = item;
      this.isLoading = true;

      setTimeout(() => {
        historyHelpers.prepareHistoryDataForGanttView(+this.$route.params.projectId, this.selectedEvent).then(ganttData => {
          this.isLoading = false;

          if (ganttData) {
            this.ganttInstance = ganttViewConstructor.initGanttInstanceHistory(+this.$route.params.projectId, ganttData);
          }
        });
      }, 300);
    },
    preparedEventData(data) {
      let eventData = data;

      if (this.searchStr) {
        eventData = this.filterEventsBySearchString(data);
      }
      eventData = this.filterEventsByProperties(eventData);

      this.hasChanged(this.currentFilter.properties);
      this.groupEventData(eventData);
    },
    filterEventsByProperties(data) {
      const properties = this.currentFilter.properties;

      const parse = x => parseInt(x || 0);

      const getValues = (element, isNumber) => {
        if (_.isString(element)) {
          return element.split(',').map(parse);
        }

        if (_.isArray(element)) {
          return isNumber ? element.map(parse) : element;
        }

        return [];
      };

      const events = _.orderBy(getValues(properties.event, true));
      const assignee = _.orderBy(getValues(properties.resource_id, true));
      const dateFrom = properties.date.start;
      const dateTo = properties.date.end;

      data = dateFrom && data.filter(item => moment(item.created_at) >= moment(dateFrom));
      data = dateTo && data.filter(item => moment(item.created_at) <= moment(dateTo));

      if (assignee.length && !assignee.includes(0)) {
        this.activeFilter = true;
        const assigneeUserIds = assignee
          .map(a => this.$store.getters['resourcesModel/getResourceById'](a))
          .map(i => i.userId);

        data = data.filter(i => assigneeUserIds.includes(i.user_id));
      }

      if (events.length && !events.includes(0)) {
        this.activeFilter = true;
        data = data.filter(item => {
          if (item.customTemplate) {
            return true;
          }

          const eventType = Math.floor(item.action_id / 10);

          if (item.action_id && eventType === 7 && item.action_id !== 71) {
            return false;
          }

          if (item.action_id === 101) {
            const eventName = item.action_description.data.eventName;

            if (eventName === 'priority' && events.includes(10)) {
              return true;
            }
            if (eventName === 'status' && events.includes(101)) {
              return true;
            }

            return false;
          }

          if (eventType === 1 || eventType === 2) { // work days
            return events.includes(2);
          }

          return events.includes(eventType);
        });
      }

      return data;
    },
    filterEventsBySearchString(events) {
      return events.filter(item => {
        const value = item.searchText?.toLowerCase();

        if (value?.includes(this.searchStr.toLowerCase())) {
          return item;
        }

        return false;
      });
    },
    groupEventData(data) {
      // const randomData = data.map(item => ({
      //   ...item,
      //   created_at: Date.now() - Math.random() * 1000 * 60 * 60 * 24 * 30 * 6,
      // }));
      const groupedByDate = data.reduce((acc, item) => {
        if (!item.created_at) return acc;
        const date = item.created_at && moment(item.created_at).format('Do MMMM YYYY');

        if (!date) {
          return acc;
        }
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);

        return acc;
      }, {});

      const result = {
        all: groupedByDate,
        hours: {},
        days: {},
        months: {},
      };

      data.forEach(item => {
        if (!item.created_at) return;
        moment.locale(user.locale);
        const date = new Date(item.created_at);
        const year = date.getFullYear();
        const month = moment(date).format('MMMM YYYY').charAt(0).toUpperCase() + moment(date).format('MMMM YYYY').slice(1);
        const dayFormatted = moment(date).format('Do MMMM YYYY');
        const hour = date.getHours();
        const dayKey = moment(date).format('DD-MM-YYYY');

        if (!result.hours[dayFormatted]) {
          result.hours[dayFormatted] = {};
        }
        const hourKey = `${hour < 10 ? `0${hour}` : hour}:00`;

        if (!result.hours[dayFormatted][hourKey]) {
          result.hours[dayFormatted][hourKey] = { display: hourKey, count: 0, id: dayKey };
        }
        result.hours[dayFormatted][hourKey].count += 1;

        if (!result.days[month]) {
          result.days[month] = {};
        }
        if (!result.days[month][dayKey]) {
          result.days[month][dayKey] = { display: dayFormatted, count: 0, id: dayKey };
        }
        result.days[month][dayKey].count += 1;

        if (!result.months[year]) {
          result.months[year] = {};
        }
        if (!result.months[year][month]) {
          result.months[year][month] = { display: month, count: 0, id: dayKey };
        }
        result.months[year][month].count += 1;
      });
      this.dateForPin = Object.keys(result.all)[0];

      this.sortedEvents = result;
    },
    getStartAndFinishDate(events) {
      const initialStartDate = new Date(2200, 0, 1);
      const initialEndDate = new Date(1900, 0, 1);

      return events.reduce((acc, historyData) => {
        const currentDate = new Date(historyData.created_at);

        if (currentDate < acc.start) {
          acc.start = currentDate;
        }
        if (currentDate > acc.end) {
          acc.end = currentDate;
        }

        return acc;
      }, {
        start: initialStartDate,
        end: initialEndDate,
      });
    },
    hasChanged(obj) {
      const defaultValues = {
        date: this.getStartAndFinishDate(this.events),
        time: this.getStartAndFinishDate(this.events),
      };

      function areDatesDifferent(date1, date2) {
        return new Date(date1).getTime() !== new Date(date2).getTime();
      }

      const isOtherFieldFilled = !!obj.event || !!obj.resource_id;

      const dateChanged = areDatesDifferent(obj.date.start, defaultValues.date.start)
                    || areDatesDifferent(obj.date.end, defaultValues.date.end);

      const timeChanged = areDatesDifferent(obj.time.start, defaultValues.time.start)
                    || areDatesDifferent(obj.time.end, defaultValues.time.end);

      this.activeFilter = isOtherFieldFilled || dateChanged || timeChanged;
    },
    setFiltersDate() {
      this.startEndDates = this.getStartAndFinishDate(this.events);
      this.$store.commit('filter/updateCurrentFilter', {
        id: null,
        name: null,
        properties: { date: this.startEndDates, time: this.startEndDates },
      });
    },
  },
};
</script>

<style src="./style.less" scoped></style>
